// Client data layer for the Client Understanding ledger (Phase 4 UI support).
// Encodes the "one fact, two directives" model: a fact is an `originId` group whose claims may carry
// a `metric.*` directive (ratios) AND/OR a `forecast.*` directive (forecast). The CfoUnderstandingPanel
// + CfoAnalysisProfilePage render these groups so a belief's dual reach is visible and one verdict
// moves both lenses.

/**
 * One raw Client Understanding claim as the server serializes it (snake_case), from
 * `GET /workbooks/:id/understanding`. Kept self-contained here (rather than imported from the data
 * layer) so the mapper has no cross-module dependency. `ServerApiClient.getQuickBooksUnderstanding`
 * returns `{ workbook_id, claims: ServerUnderstandingClaim[] }`.
 */
export type ServerUnderstandingClaim = {
  id: string
  workbook_id: string
  connection_id: string
  realm_id: string
  scenario_key: string
  claim_type: string
  /** 'forecast.*' | 'metric.*' */
  directive_kind: string
  business_key: string
  origin_id: string
  supersedes_id: string | null
  assertion: string
  directive_json: unknown
  evidence_json: unknown
  confidence: number | null
  source: string
  status: string
  /** Believability-gate score for a forecast belief: 1 = passed, 0 = auto-applied + flagged. Null for
   *  metric beliefs (not gated). */
  gate_score: number | null
  /** When the owner ACCEPTED this assumption (panel Accept / Accept all). Null = not acknowledged. */
  acknowledged_at?: string | null
  revision: number
}

export type UnderstandingClaim = {
  id: string
  workbookId: string
  scenarioKey: string
  claimType: string
  /** 'forecast.*' | 'metric.*' */
  directiveKind: string
  businessKey: string
  originId: string
  supersedesId: string | null
  assertion: string
  directiveJson: unknown
  evidenceJson: unknown
  confidence: number | null
  source: string
  status: string
  gateScore: number | null
  /** ISO timestamp when the owner accepted this assumption; null = still open in the panel. */
  acknowledgedAt: string | null
  revision: number
}

/** Map one raw server claim (snake_case wire shape) to the camelCase UI claim. */
export function mapServerClaim(raw: ServerUnderstandingClaim): UnderstandingClaim {
  return {
    id: raw.id,
    workbookId: raw.workbook_id,
    scenarioKey: raw.scenario_key,
    claimType: raw.claim_type,
    directiveKind: raw.directive_kind,
    businessKey: raw.business_key,
    originId: raw.origin_id,
    supersedesId: raw.supersedes_id ?? null,
    assertion: raw.assertion,
    directiveJson: raw.directive_json,
    evidenceJson: raw.evidence_json,
    confidence: raw.confidence ?? null,
    source: raw.source,
    status: raw.status,
    gateScore: raw.gate_score ?? null,
    acknowledgedAt: raw.acknowledged_at ?? null,
    revision: raw.revision,
  }
}

/** A belief is FLAGGED when the AI auto-applied it (it's active) despite the believability gate not
 *  clearing it — a low gate score. These are the opinionated "we made this assumption, but it's worth
 *  a look" beliefs the user may want to correct. Metric beliefs (gateScore null) are never flagged. */
export function isClaimFlagged(claim: UnderstandingClaim): boolean {
  return claim.status === 'active' && claim.gateScore != null && claim.gateScore < 0.5
}

/** Is any directive in this fact group flagged-for-review? */
export function factIsFlagged(group: UnderstandingClaim[]): boolean {
  return group.some(isClaimFlagged)
}

/** Group claims into "facts" — all directives sharing an `originId`. Preserves input order. */
export function groupClaimsByOrigin(
  claims: UnderstandingClaim[],
): Map<string, UnderstandingClaim[]> {
  const groups = new Map<string, UnderstandingClaim[]>()
  for (const claim of claims) {
    const existing = groups.get(claim.originId)
    if (existing) {
      existing.push(claim)
    } else {
      groups.set(claim.originId, [claim])
    }
  }
  return groups
}

/** Human "what it drives" label for a fact group, e.g. `affects: ratios + forecast`. */
export function claimReachLabel(group: UnderstandingClaim[]): string {
  const hasForecast = group.some((c) => c.directiveKind.startsWith('forecast.'))
  const hasMetric = group.some((c) => c.directiveKind.startsWith('metric.'))
  const reach: string[] = []
  if (hasMetric) reach.push('ratios')
  if (hasForecast) reach.push('forecast')
  return reach.length > 0 ? `affects: ${reach.join(' + ')}` : 'no active effect'
}

export type AutonomyVerdict = 'activate' | 'hold'

/** Forecast-directive autonomy (plan §1.4): gate-then-promote — auto-activate ONLY if the regenerated
 *  forecast cleared the believability gate; otherwise hold for human review (never silently dropped).
 *  The UI renders "active" vs "held pending review" from this. */
export function forecastAutonomyVerdict(believable: boolean): AutonomyVerdict {
  return believable ? 'activate' : 'hold'
}

/** Metric-directive autonomy (plan §1.4): apply-on-write — a wrong band is cosmetic and one-click
 *  revertible, so metric claims activate immediately. */
export function metricAutonomyVerdict(): AutonomyVerdict {
  return 'activate'
}

/** The active claims a redirect to `originId` would supersede — the WHOLE origin group, atomically
 *  (plan §Part 4: one redirect moves one fact so both compilers recompute coherently and neither
 *  half can be left stale). Excludes already-superseded/rejected rows. */
export function claimsToSupersede(
  claims: UnderstandingClaim[],
  originId: string,
): UnderstandingClaim[] {
  return claims.filter((c) => c.originId === originId && c.status === 'active')
}
